import { useEffect, useState } from "react";
import clsx from "clsx";

const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.0;

type ZoomControlsProps = {
  className?: string;
};

/**
 * Floating zoom in / zoom out / reset buttons.
 *
 * Uses `document.body.style.zoom` — a non-standard CSS property that
 * Chromium, Safari, and recent Firefox respect. It reflows content
 * (unlike `transform: scale`) so the gameplay layout stays usable
 * at higher zoom levels.
 *
 * The body zoom is reset back to 100% when this component unmounts
 * so leaving the game returns the rest of the app to normal size.
 */
export const ZoomControls = ({ className }: ZoomControlsProps) => {
  const [zoom, setZoom] = useState<number>(1);

  // Apply zoom to the document body whenever the level changes.
  useEffect(() => {
    // `zoom` isn't in the TS DOM types, so cast through `any`.
    (document.body.style as any).zoom = String(zoom);
    return () => {
      (document.body.style as any).zoom = "1";
    };
  }, [zoom]);

  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)));
  const reset = () => setZoom(1);

  const percent = Math.round(zoom * 100);

  return (
    <div
      className={clsx(
        "fixed top-3 right-3 z-50 flex items-center gap-1 bg-white/90 backdrop-blur rounded-full shadow-lg border border-gray-300 px-2 py-1",
        className,
      )}
    >
      <button
        type="button"
        onClick={zoomOut}
        disabled={zoom <= MIN_ZOOM}
        aria-label="Zoom out"
        className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold text-gray-700 hover:bg-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent"
      >
        −
      </button>
      <button
        type="button"
        onClick={reset}
        aria-label="Reset zoom"
        className="text-xs font-medium text-gray-600 hover:text-gray-900 min-w-[3rem] text-center tabular-nums"
        title="Reset zoom"
      >
        {percent}%
      </button>
      <button
        type="button"
        onClick={zoomIn}
        disabled={zoom >= MAX_ZOOM}
        aria-label="Zoom in"
        className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold text-gray-700 hover:bg-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent"
      >
        +
      </button>
    </div>
  );
};
